import { Component, ElementRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { LinkCreateModel } from '../../../models/link.model';
import { SeminarFilterOptionModel } from '../../../models/seminar.model';
import { TagModel } from '../../../models/tag.model';
import { isValidFileType } from '../../../utils/file.utils';
import { fieldRequired } from '../../../utils/form.utils';
import { getSeminarFirstDateString } from '../../../utils/seminar.utils';

export interface UploadAssetDialogResult {
  type: 'file' | 'link';
  file?: File;
  link?: LinkCreateModel;
  tags: TagModel[];
  seminarIds: number[];
}

@Component({
  selector: 'app-upload-asset-dialog',
  templateUrl: './upload-asset-dialog.component.html',
  styleUrl: './upload-asset-dialog.component.scss',
  standalone: false,
})
export class UploadAssetDialogComponent {
  @ViewChild('fileInput') private fileInput?: ElementRef<HTMLInputElement>;
  public fieldRequired = fieldRequired;
  public type: 'file' | 'link' = 'file';
  public seminarOptions: SeminarFilterOptionModel[] = [];
  public selectedTags: TagModel[] = [];
  public selectedFile: File | null = null;

  public getSeminarDateString = getSeminarFirstDateString;

  public form = new FormGroup({
    linkTitle: new FormControl<string>(''),
    linkUrl: new FormControl<string>(''),
    seminarIds: new FormControl<number[]>([], Validators.required),
    tags: new FormControl<TagModel[]>([], Validators.required),
  });

  constructor(
    private dialogRef: DynamicDialogRef,
    private config: DynamicDialogConfig
  ) {
    this.type = this.config.data?.type ?? 'file';
    this.seminarOptions = this.config.data?.seminarOptions ?? [];
    this.configureTypeValidators();
  }

  public onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    if (!file) {
      this.selectedFile = null;
      return;
    }

    if (!isValidFileType(file)) {
      this.selectedFile = null;
      return;
    }

    this.selectedFile = file;
  }

  public triggerFileSelect(): void {
    this.fileInput?.nativeElement.click();
  }

  public selectedTagsChange(tags: TagModel[]): void {
    this.selectedTags = tags;
    this.form.patchValue({ tags });
    this.form.get('tags')?.updateValueAndValidity();
  }

  public closeDialog(): void {
    this.dialogRef.close();
  }

  public submit(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid) return;
    if (this.type === 'file' && !this.selectedFile) return;

    const result: UploadAssetDialogResult = {
      type: this.type,
      tags: this.form.value.tags ?? [],
      seminarIds: this.form.value.seminarIds ?? [],
    };

    if (this.type === 'file') {
      result.file = this.selectedFile;
    } else {
      result.link = {
        title: this.form.value.linkTitle ?? '',
        url: this.form.value.linkUrl ?? '',
      };
    }

    this.dialogRef.close(result);
  }

  private configureTypeValidators(): void {
    const title = this.form.get('linkTitle');
    const url = this.form.get('linkUrl');
    if (this.type === 'link') {
      title?.setValidators([Validators.required, Validators.maxLength(255)]);
      url?.setValidators([
        Validators.required,
        Validators.pattern('https?://.+'),
        Validators.maxLength(2000),
      ]);
    } else {
      title?.clearValidators();
      url?.clearValidators();
      title?.setValue('');
      url?.setValue('');
    }
    title?.updateValueAndValidity();
    url?.updateValueAndValidity();
  }
}
